/**
 * Technical details of a media file (codec, resolution, frame rate, bitrates, audio format), read from
 * the file's own header with a few small byte-range requests, never the whole file (Aaron, 5 Nov 2026:
 * an Info button "so people can see the compression quality of the video").
 *
 * Understands MP4 / MOV / M4V / M4A (ISO base media and QuickTime) and WAV. Anything else returns null,
 * and the Info panel falls back to what the browser can tell from the file (resolution, length).
 */

export type TrackInfo = {
  kind: 'video' | 'audio';
  codec: string; // the four-character code, e.g. avc1, apch, mp4a
  codecName: string; // e.g. "H.264 High profile, level 4.0"
  width?: number;
  height?: number;
  frameRate?: number;
  bitrate?: number; // bits per second
  sampleRate?: number;
  channels?: number;
  bitDepth?: number;
  duration?: number; // seconds
};

export type MediaInfo = {
  container: string;
  duration?: number;
  overallBitrate?: number;
  tracks: TrackInfo[];
};

/** Reads bytes start..end (inclusive). */
export type ReadRange = (start: number, end: number) => Promise<DataView>;

const MAX_MOOV = 64 * 1024 * 1024;

const fourcc = (v: DataView, at: number) => String.fromCharCode(v.getUint8(at), v.getUint8(at + 1), v.getUint8(at + 2), v.getUint8(at + 3));
const u64 = (v: DataView, at: number) => v.getUint32(at) * 4294967296 + v.getUint32(at + 4);

const H264_PROFILES: Record<number, string> = { 66: 'Baseline', 77: 'Main', 88: 'Extended', 100: 'High', 110: 'High 10', 122: 'High 4:2:2', 244: 'High 4:4:4' };
const HEVC_PROFILES: Record<number, string> = { 1: 'Main', 2: 'Main 10', 3: 'Main Still Picture', 4: 'Range Extensions' };
const VIDEO_CODECS: Record<string, string> = {
  avc1: 'H.264',
  avc3: 'H.264',
  hvc1: 'H.265 (HEVC)',
  hev1: 'H.265 (HEVC)',
  apco: 'Apple ProRes 422 Proxy',
  apcs: 'Apple ProRes 422 LT',
  apcn: 'Apple ProRes 422',
  apch: 'Apple ProRes 422 HQ',
  ap4h: 'Apple ProRes 4444',
  ap4x: 'Apple ProRes 4444 XQ',
  AVdn: 'Avid DNxHD',
  AVdh: 'Avid DNxHD / DNxHR',
  mp4v: 'MPEG-4 Part 2',
  jpeg: 'Motion JPEG',
  mjpa: 'Motion JPEG',
  mjpb: 'Motion JPEG',
  av01: 'AV1',
  vp09: 'VP9',
  'xd5e': 'XDCAM HD422',
  'xd5c': 'XDCAM HD422',
};
const AUDIO_CODECS: Record<string, string> = {
  mp4a: 'AAC',
  lpcm: 'PCM (uncompressed)',
  sowt: 'PCM (uncompressed)',
  twos: 'PCM (uncompressed)',
  in24: 'PCM (uncompressed)',
  in32: 'PCM (uncompressed)',
  fl32: 'PCM (32-bit float)',
  fl64: 'PCM (64-bit float)',
  raw: 'PCM (uncompressed)',
  'ac-3': 'Dolby Digital (AC-3)',
  'ec-3': 'Dolby Digital Plus (E-AC-3)',
  alac: 'Apple Lossless',
  ima4: 'IMA ADPCM',
  Opus: 'Opus',
  '.mp3': 'MP3',
};

type Box = { type: string; start: number; end: number; body: number }; // offsets within a DataView

/** The boxes directly inside v[from, to). */
const children = (v: DataView, from: number, to: number): Box[] => {
  const out: Box[] = [];
  let at = from;
  while (at + 8 <= to) {
    let size = v.getUint32(at);
    const type = fourcc(v, at + 4);
    let body = at + 8;
    if (size === 1) {
      size = u64(v, at + 8);
      body = at + 16;
    } else if (size === 0) size = to - at;
    if (size < 8 || at + size > to) break;
    out.push({ type, start: at, end: at + size, body });
    at += size;
  }
  return out;
};
const child = (v: DataView, box: Box | undefined, type: string, skip = 0) =>
  box ? children(v, box.body + skip, box.end).find((b) => b.type === type) : undefined;

type RawTrack = {
  handler?: string;
  timescale?: number;
  duration?: number;
  width?: number;
  height?: number;
  codec?: string;
  codecName?: string;
  sampleRate?: number;
  channels?: number;
  bitDepth?: number;
  samples?: number;
  sampleTime?: number;
  bytes?: number;
  btrt?: number;
};

const parseTrak = (v: DataView, trak: Box): RawTrack => {
  const t: RawTrack = {};
  const tkhd = child(v, trak, 'tkhd');
  if (tkhd) {
    const ver = v.getUint8(tkhd.body);
    const at = tkhd.body + 4 + (ver === 1 ? 84 : 72);
    if (at + 8 <= tkhd.end) {
      t.width = Math.round(v.getUint32(at) / 65536);
      t.height = Math.round(v.getUint32(at + 4) / 65536);
    }
  }
  const mdia = child(v, trak, 'mdia');
  const mdhd = child(v, mdia, 'mdhd');
  if (mdhd) {
    const ver = v.getUint8(mdhd.body);
    if (ver === 1) {
      t.timescale = v.getUint32(mdhd.body + 20);
      t.duration = u64(v, mdhd.body + 24);
    } else {
      t.timescale = v.getUint32(mdhd.body + 12);
      t.duration = v.getUint32(mdhd.body + 16);
    }
  }
  const hdlr = child(v, mdia, 'hdlr');
  if (hdlr) t.handler = fourcc(v, hdlr.body + 8);
  const stbl = child(v, child(v, mdia, 'minf'), 'stbl');
  const stsd = child(v, stbl, 'stsd');
  if (stsd && stsd.body + 16 <= stsd.end) {
    const entry = children(v, stsd.body + 8, stsd.end)[0];
    if (entry) {
      t.codec = entry.type;
      const e = entry.start + 16; // after size, type, 6 reserved bytes and the data reference index
      if (t.handler === 'vide' && e + 70 <= entry.end) {
        t.width = v.getUint16(e + 16) || t.width;
        t.height = v.getUint16(e + 18) || t.height;
        const depth = v.getUint16(e + 66);
        if (depth && depth <= 48) t.bitDepth = depth;
        const kids = children(v, e + 70, entry.end);
        const avcC = kids.find((b) => b.type === 'avcC');
        const hvcC = kids.find((b) => b.type === 'hvcC');
        const btrt = kids.find((b) => b.type === 'btrt');
        if (avcC) {
          const profile = v.getUint8(avcC.body + 1);
          const level = v.getUint8(avcC.body + 3);
          t.codecName = `H.264 ${H264_PROFILES[profile] || `profile ${profile}`} profile, level ${(level / 10).toFixed(1)}`;
        } else if (hvcC) {
          const profile = v.getUint8(hvcC.body + 1) & 0x1f;
          const level = v.getUint8(hvcC.body + 12);
          t.codecName = `H.265 (HEVC) ${HEVC_PROFILES[profile] || `profile ${profile}`} profile, level ${(level / 30).toFixed(1)}`;
        }
        if (btrt) t.btrt = v.getUint32(btrt.body + 8);
      } else if (t.handler === 'soun' && e + 20 <= entry.end) {
        const qtVersion = v.getUint16(e);
        if (qtVersion === 2 && e + 56 <= entry.end) {
          t.sampleRate = Math.round(v.getFloat64(e + 24));
          t.channels = v.getUint32(e + 32);
          t.bitDepth = v.getUint32(e + 40);
        } else {
          t.channels = v.getUint16(e + 8);
          t.bitDepth = v.getUint16(e + 10);
          t.sampleRate = Math.round(v.getUint32(e + 16) / 65536);
        }
        if (t.codec === 'mp4a' || t.codec === 'ac-3' || t.codec === 'ec-3') t.bitDepth = undefined; // not meaningful for compressed audio
      }
      if (!t.codecName) {
        const names = t.handler === 'soun' ? AUDIO_CODECS : VIDEO_CODECS;
        t.codecName = names[entry.type] || names[entry.type.trim()] || entry.type.trim().toUpperCase();
      }
    }
  }
  const stts = child(v, stbl, 'stts');
  if (stts) {
    const n = v.getUint32(stts.body + 4);
    let samples = 0;
    let time = 0;
    for (let i = 0; i < n && stts.body + 8 + i * 8 + 8 <= stts.end; i++) {
      const count = v.getUint32(stts.body + 8 + i * 8);
      samples += count;
      time += count * v.getUint32(stts.body + 12 + i * 8);
    }
    t.samples = samples;
    t.sampleTime = time;
  }
  const stsz = child(v, stbl, 'stsz');
  if (stsz) {
    const fixed = v.getUint32(stsz.body + 4);
    const count = v.getUint32(stsz.body + 8);
    if (fixed) t.bytes = fixed * count;
    else {
      let sum = 0;
      for (let i = 0; i < count && stsz.body + 12 + i * 4 + 4 <= stsz.end; i++) sum += v.getUint32(stsz.body + 12 + i * 4);
      t.bytes = sum;
    }
  }
  return t;
};

async function readIsoBmff(read: ReadRange, size: number): Promise<MediaInfo | null> {
  // Walk the top-level boxes (one small read each) until the movie header box ("moov").
  let at = 0;
  let brand = '';
  for (let i = 0; i < 64 && at + 8 <= size; i++) {
    const head = await read(at, Math.min(at + 15, size - 1));
    let boxSize = head.getUint32(0);
    const type = fourcc(head, 4);
    if (i === 0 && type !== 'ftyp' && type !== 'moov' && type !== 'wide' && type !== 'mdat' && type !== 'free' && type !== 'skip') return null;
    if (type === 'ftyp' && head.byteLength >= 12) brand = fourcc(head, 8);
    if (boxSize === 1 && head.byteLength >= 16) boxSize = u64(head, 8);
    else if (boxSize === 0) boxSize = size - at;
    if (boxSize < 8) return null;
    if (type === 'moov') {
      if (boxSize > MAX_MOOV) return null;
      const moovView = await read(at, at + boxSize - 1);
      const moov: Box = { type, start: 0, end: moovView.byteLength, body: 8 };
      return fromMoov(moovView, moov, brand, size);
    }
    at += boxSize;
  }
  return null;
}

const fromMoov = (v: DataView, moov: Box, brand: string, fileSize: number): MediaInfo => {
  let duration: number | undefined;
  const mvhd = child(v, moov, 'mvhd');
  if (mvhd) {
    const ver = v.getUint8(mvhd.body);
    const timescale = v.getUint32(mvhd.body + (ver === 1 ? 20 : 12));
    const d = ver === 1 ? u64(v, mvhd.body + 24) : v.getUint32(mvhd.body + 16);
    if (timescale) duration = d / timescale;
  }
  const tracks: TrackInfo[] = [];
  children(v, moov.body, moov.end)
    .filter((b) => b.type === 'trak')
    .forEach((trak) => {
      const t = parseTrak(v, trak);
      if (t.handler !== 'vide' && t.handler !== 'soun') return; // timecode, data and text tracks
      const seconds = t.timescale && t.duration ? t.duration / t.timescale : undefined;
      const info: TrackInfo = {
        kind: t.handler === 'vide' ? 'video' : 'audio',
        codec: (t.codec || '').trim(),
        codecName: t.codecName || 'Unknown',
        duration: seconds,
        bitrate: t.bytes && seconds ? (t.bytes * 8) / seconds : t.btrt || undefined,
      };
      if (info.kind === 'video') {
        info.width = t.width;
        info.height = t.height;
        if (t.samples && t.sampleTime && t.timescale) info.frameRate = (t.samples * t.timescale) / t.sampleTime;
        if (t.bitDepth && t.bitDepth !== 24 && t.bitDepth !== 32) info.bitDepth = t.bitDepth;
      } else {
        info.sampleRate = t.sampleRate;
        info.channels = t.channels;
        info.bitDepth = t.bitDepth;
      }
      tracks.push(info);
    });
  const container = brand === 'qt  ' ? 'QuickTime (MOV)' : brand.startsWith('M4A') ? 'MPEG-4 audio (M4A)' : brand ? `MPEG-4 (${brand.trim()})` : 'MPEG-4 / QuickTime';
  return { container, duration, overallBitrate: duration ? (fileSize * 8) / duration : undefined, tracks };
};

async function readWav(read: ReadRange, size: number): Promise<MediaInfo | null> {
  const v = await read(0, Math.min(65535, size - 1));
  if (v.byteLength < 12 || fourcc(v, 0) !== 'RIFF' || fourcc(v, 8) !== 'WAVE') return null;
  let at = 12;
  let fmt: TrackInfo | null = null;
  let dataBytes = 0;
  let byteRate = 0;
  while (at + 8 <= v.byteLength) {
    const id = fourcc(v, at);
    const len = v.getUint32(at + 4, true);
    if (id === 'fmt ' && at + 24 <= v.byteLength) {
      const format = v.getUint16(at + 8, true);
      byteRate = v.getUint32(at + 16, true);
      fmt = {
        kind: 'audio',
        codec: `wav${format}`,
        codecName: format === 1 || format === 0xfffe ? 'PCM (uncompressed)' : format === 3 ? 'PCM (32-bit float)' : format === 0x55 ? 'MP3' : `WAV format ${format}`,
        channels: v.getUint16(at + 10, true),
        sampleRate: v.getUint32(at + 12, true),
        bitDepth: v.getUint16(at + 22, true),
        bitrate: byteRate * 8,
      };
    } else if (id === 'data') {
      dataBytes = len;
      break;
    }
    at += 8 + len + (len % 2);
  }
  if (!fmt) return null;
  const duration = byteRate && dataBytes ? dataBytes / byteRate : undefined;
  fmt.duration = duration;
  return { container: 'WAV', duration, overallBitrate: duration ? (size * 8) / duration : undefined, tracks: [fmt] };
}

/** Technical details for a file, or null if its format isn't one this can read. */
export async function readMediaInfo(read: ReadRange, size: number, fileName: string): Promise<MediaInfo | null> {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (ext === 'wav' || ext === 'wave') return readWav(read, size);
  if (['mp4', 'mov', 'm4v', 'm4a', '3gp', 'qt'].includes(ext)) return readIsoBmff(read, size);
  return null;
}

/** A ReadRange over a URL, using HTTP range requests. Stops at once if the server ignores the range. */
export const httpRange = (url: string): ReadRange => async (start, end) => {
  const controller = new AbortController();
  const res = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, signal: controller.signal });
  if (res.status !== 206) {
    controller.abort(); // a full download was coming back: don't fetch the whole file
    throw new Error(`The file server didn't allow a partial read (${res.status}).`);
  }
  return new DataView(await res.arrayBuffer());
};

/** "1080p" style shorthand plus the exact size. */
export const describeResolution = (w?: number, h?: number) => {
  if (!w || !h) return '—';
  const tag = h >= 2160 || w >= 3840 ? ' (4K UHD)' : h === 1080 || w === 1920 ? ' (Full HD)' : h === 720 || w === 1280 ? ' (HD)' : '';
  return `${w} × ${h}${tag}`;
};

export const describeRate = (bps?: number) => {
  if (!bps || !isFinite(bps)) return '—';
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(bps >= 1e8 ? 0 : 1)} Mb/s`;
  return `${Math.round(bps / 1000)} kb/s`;
};

export const describeFrameRate = (fps?: number) => {
  if (!fps || !isFinite(fps)) return '—';
  const known = [23.976, 24, 25, 29.97, 30, 47.952, 48, 50, 59.94, 60, 100, 119.88, 120];
  const near = known.find((k) => Math.abs(k - fps) < 0.01);
  return `${near !== undefined ? near : Math.round(fps * 1000) / 1000} fps`;
};

/**
 * How much data each pixel gets per frame. A common rule of thumb for how hard the picture is compressed.
 * Intra-frame codecs (ProRes, DNx) are high quality by design, so they get no rating.
 */
export const compressionNote = (track: TrackInfo): { bpp?: number; note: string } | null => {
  if (track.kind !== 'video' || !track.width || !track.height || !track.frameRate || !track.bitrate) return null;
  const bpp = track.bitrate / (track.width * track.height * track.frameRate);
  if (/ProRes|DNx|XDCAM|Motion JPEG/.test(track.codecName)) return { bpp, note: 'Edit-quality codec: every frame stored whole, lightly compressed.' };
  if (bpp >= 0.2) return { bpp, note: 'Light compression: high quality.' };
  if (bpp >= 0.08) return { bpp, note: 'Moderate compression: typical for delivery files.' };
  if (bpp >= 0.04) return { bpp, note: 'Strong compression: typical for web and social.' };
  return { bpp, note: 'Heavy compression: a small viewing copy, not master quality.' };
};
